<template>
  <section class="game-home-page">
    <div ref="cardsListRef" class="preview-cards-list">
      <router-link v-if="canReadClasses" class="card" :to="goTo('classes')">
        <div class="icon">
          <VdIcon store="fa" name="rectangle-code" size="large" />
          <div class="glow"></div>
        </div>
        <div class="title">
          <h2>Classes</h2>
        </div>
        <div class="description">
          <p>Create, edit or import the remotely-configurable classes of your game.</p>
        </div>
      </router-link>

      <router-link v-if="canReadSegments" class="card" :to="goTo('segments')">
        <div class="icon">
          <VdIcon store="fa" name="bullseye-arrow" size="large" />
          <div class="glow"></div>
        </div>
        <div class="title">
          <h2>Segments</h2>
        </div>
        <div class="description">
          <p>
            Create or edit a segment (part of your audience sharing common characteristics). These segments will be used
            in the "Configurations" section and the "A/B tests" section.
          </p>
        </div>
      </router-link>

      <router-link v-if="canReadFeatures" class="card" :to="goTo('features')">
        <div class="icon">
          <VdIcon store="fa" name="key-skeleton-left-right" size="large" />
          <div class="glow"></div>
        </div>
        <div class="title">
          <h2>Metrics</h2>
        </div>
        <div class="description">
          <p>
            Create, edit or import a segmentation metric. These segmentation metrics will be used to define entry
            conditions to your segments.
          </p>
        </div>
      </router-link>

      <router-link v-if="canReadAbtests" class="card" :to="goTo('abtests')">
        <div class="icon">
          <VdIcon store="fa" name="split" size="large" />
          <div class="glow"></div>
        </div>
        <div class="title">
          <h2>A/B Tests</h2>
        </div>
        <div class="description">
          <p>
            Create, edit or stop an A/B test. The results of these tests will enable you to define the optimal game
            configuration for all or part of your users.
          </p>
        </div>
      </router-link>

      <router-link v-if="canReadConfigurations" class="card" :to="goTo('configurations')">
        <div class="icon">
          <VdIcon store="fa" name="sliders" size="large" />
          <div class="glow"></div>
        </div>
        <div class="title">
          <h2>Remote Configs</h2>
        </div>
        <div class="description">
          <p>Tailor the gaming experience to your diverse audience.</p>
        </div>
      </router-link>

      <router-link v-if="canReadTesters" class="card" :to="goTo('testDevices')">
        <div class="icon">
          <VdIcon store="fa" name="address-book" size="large" />
          <div class="glow"></div>
        </div>
        <div class="title">
          <h2>Test device assignations</h2>
        </div>
        <div class="description">
          <p>
            Manage the list of your test device assignations, having the ability to access the in-game debugger for QA
            purposes.
          </p>
        </div>
      </router-link>

      <router-link v-if="canReadActionlogs" class="card" :to="goTo('actionLogs')">
        <div class="icon">
          <VdIcon store="fa" name="file-lines" size="large" />
          <div class="glow"></div>
        </div>
        <div class="title">
          <h2>Action Logs</h2>
        </div>
        <div class="description">
          <p>Find all the history of user actions performed on each version of your game.</p>
        </div>
      </router-link>
    </div>
  </section>
</template>

<script lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { useRafFn } from "@vueuse/core";
import { defineComponent, onMounted, ref } from "vue";

import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";

export default defineComponent({
  name: "GameHomePage",

  components: {
    VdIcon,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },
    versionId: {
      type: String,
      required: true,
    },
  },

  setup() {
    const {
      canReadSegments,
      canReadConfigurations,
      canReadAbtests,
      canReadFeatures,
      canReadClasses,
      canReadTesters,
      canReadSandboxes,
      canReadActionlogs,
    } = usePermissions();

    const cardsListRef = ref<HTMLElement>();

    const goTo = (routeName: keyof typeof links.games) => {
      const location: string | { list: string } = links.games[routeName];
      if (typeof location === "object") {
        return { name: location.list };
      }
      if (typeof location === "string") {
        return { name: location };
      }
      return { name: links.games.home };
    };

    onMounted(() => {
      const cards = cardsListRef.value?.querySelectorAll<HTMLElement>(".card") ?? [];
      /**
       * Use a coefficient to fine-tune the animation
       */
      const range = 20;

      const calcValue = (a: number, b: number) => (((a / b) * range) / 2).toFixed(2);

      /**
       * This part is just to animate the icons according to the mouse position inside a card
       */
      cards.forEach((card: HTMLElement) => {
        let x: number;
        let y: number;

        const icon = card.querySelector<HTMLElement>(".icon");
        const glow = card.querySelector<HTMLElement>(".glow");

        if (!icon || !glow) {
          return;
        }

        const animateIcon = () => {
          const xValue = calcValue(x, icon.offsetWidth);
          const yValue = calcValue(y, icon.offsetHeight);

          icon.style.transform = `translateX(${parseFloat(xValue) / 6}px) translateY(${parseFloat(yValue) / 6}px)`;
          glow.style.transform = `translateX(${xValue}px) translateY(${yValue}px)`;
        };

        const { pause: pauseAnimation, resume: resumeAnimation } = useRafFn(animateIcon);

        card.addEventListener(
          "mousemove",
          (event) => {
            /**
             * set origin of mouse to the center of the container
             */
            const _x = card.offsetLeft + Math.floor(card.offsetWidth / 2);
            const _y = card.offsetTop + Math.floor(card.offsetHeight / 2);

            x = event.pageX - _x;
            y = event.pageY - _y;

            resumeAnimation();
          },
          false
        );

        /**
         * On mouse leave reset the position of the icon
         */
        card.addEventListener(
          "mouseout",
          () => {
            pauseAnimation();
            icon.style.transform = "translateX(0) translateY(0)";
            glow.style.transform = "translateX(0) translateY(0)";
          },
          false
        );
      });
    });

    return {
      goTo,
      cardsListRef,
      canReadSegments,
      canReadConfigurations,
      canReadAbtests,
      canReadFeatures,
      canReadClasses,
      canReadTesters,
      canReadSandboxes,
      canReadActionlogs,
    };
  },
});
</script>

<style lang="scss">
.game-home-page {
  .preview-cards-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(200px, 1fr));

    @include small-screen {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
    gap: 20px;

    .card {
      @extend %card;
      padding: size("L");
      display: grid;
      place-items: center;
      position: relative;
      will-change: transform;
      transition: transform 0.5s ease-in-out;

      &:hover {
        transform: scale(1.005);

        .icon {
          transform: scale(1.02);
        }
      }

      .icon {
        @include center("both", "grid");
        background-color: $cloudy-blue;
        width: 50px;
        height: 50px;
        border-radius: 15px;
        border: 2px solid var(--voodootune-highlight-color);
        margin-bottom: size("M");
        transform-origin: center center;
        transform-style: preserve-3d;
        transition: transform 0.1s linear;
        overflow: hidden;
        position: relative;
        perspective: 30px;

        .glow {
          display: block;
          position: absolute;
          opacity: 0.7;
          width: 50%;
          height: 50%;
          border-radius: 50%;
          top: 50%;
          left: 50%;
          transform-origin: center center;
          transform: translate(-50%, -50%);
          transition: transform 0.1s ease-in;
          z-index: 9999;
          background-color: $foggy-blue;
          filter: blur(09px);
        }

        svg {
          color: var(--voodootune-accent-color);
        }
      }

      .title {
        color: var(--vd-base-primary-color);

        h2 {
          font-size: font("ML");
          font-weight: 600;
        }
      }

      .description {
        text-align: center;
        color: var(--vd-base-regular-text-color);
      }
    }
  }
}
</style>
